// components/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { LogIn, Mail, Lock, Loader2, AlertCircle, Compass, Eye, EyeOff, Key } from 'lucide-react';
import { login, setNewPassword, isAuthenticated, getCurrentUser } from '../api/auth.service';

const Login = ({ setUser, onLoginSuccess }) => {
  const [step, setStep] = useState('login'); // login, new-password
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [tempData, setTempData] = useState({ new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [tempEmail, setTempEmail] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is already logged in
  useEffect(() => {
    if (isAuthenticated()) {
      const user = getCurrentUser();
      const from = location.state?.from?.pathname || getDashboardPath(user);
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  // Get dashboard path based on user role
  const getDashboardPath = (user) => {
    if (user?.role_code === 'ADMIN') return '/admin/dashboard';
    if (user?.role_code === 'CLIENT') return '/client/dashboard';
    return '/dashboard';
  };

  // Load saved email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('savedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  // --- 3D Tilt Logic ---
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await login(formData);
      
      if (response.status) {
        // Save email if remember me is checked
        if (rememberMe) {
          localStorage.setItem('savedEmail', formData.email);
        } else {
          localStorage.removeItem('savedEmail');
        }

        // Check if user needs to set new password
        if (response.is_new_user === 0 || response.is_new_user === "0") {
          setTempEmail(formData.email);
          setStep('new-password');
        } 
        else if (response.user) {
          // Store user data
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));
          
          // Store refresh token if provided
          if (response.refresh_token) {
            localStorage.setItem('refresh_token', response.refresh_token);
          }
          
          // Store navigation list if provided
          if (response.nav_list) {
            localStorage.setItem('nav_list', JSON.stringify(response.nav_list));
          }
          
          // Call success callbacks
          if (onLoginSuccess) onLoginSuccess(response.user, response.token, response.nav_list);
          if (setUser) setUser(response.user);
          
          // Redirect based on user role
          const dashboardPath = getDashboardPath(response.user);
          navigate(dashboardPath);
        } else {
          setError('Invalid response from server');
        }
      } else {
        setError(response.message || 'Invalid email or password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Connection failed. Please check your network.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Password validation
    if (tempData.new_password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }
    
    if (tempData.new_password !== tempData.confirm_password) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const response = await setNewPassword({
        email: tempEmail || formData.email,
        new_password: tempData.new_password
      });

      if (response.status) {
        alert('Password updated successfully! Please login with your new password.');
        setStep('login');
        setTempData({ new_password: '', confirm_password: '' });
        setFormData(prev => ({ ...prev, password: '' }));
      } else {
        setError(response.message || 'Failed to set password');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setError('Please contact your administrator to reset your password.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050816] overflow-hidden relative font-sans p-6">
      
      {/* Animated Background Particles */}
      <div className="absolute inset-0 z-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute bg-amber-500/10 rounded-full blur-xl"
            style={{
              width: Math.random() * 300 + 100,
              height: Math.random() * 300 + 100,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50, 0],
              x: [0, Math.random() * 100 - 50, 0],
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>

      {/* Main 3D Perspective Wrapper */}
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative z-10 max-w-5xl w-full flex flex-col md:flex-row bg-white/5 backdrop-blur-2xl rounded-[3rem] border border-white/10 shadow-[0_50px_100px_-20px_rgba(0,0,0,0.7)]"
      >
        
        {/* Left Side: Branding */}
        <div 
          style={{ transform: "translateZ(50px)" }} 
          className="md:w-5/12 p-12 flex flex-col justify-center items-center text-white border-b md:border-b-0 md:border-r border-white/10"
        >
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="mb-8 p-6 bg-gradient-to-br from-amber-400 to-orange-600 rounded-[2rem] shadow-[0_0_30px_rgba(245,158,11,0.3)]"
          >
            <Compass size={48} />
          </motion.div>
          
          <h1 className="text-4xl font-black italic tracking-tighter mb-2 text-center">TravSky</h1>
          <p className="text-slate-400 text-[10px] tracking-[0.4em] uppercase font-bold text-center">Elevating Your Journey</p>
          
          <div className="mt-12 space-y-4 w-full">
            <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
            <p className="text-slate-300 text-sm font-light text-center px-4 leading-relaxed">Experience the next dimension of travel management.</p>
          </div>

          <div className="mt-8 space-y-3 w-full">
            {['Secure Access', 'Role-Based Dashboard', '24/7 Support'].map((feature, idx) => (
              <div key={idx} className="flex items-center justify-center md:justify-start gap-3 text-sm text-slate-400">
                <div className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                {feature}
              </div>
            ))}
          </div>
        </div>

        {/* Right Side: Interactive Form */}
        <div 
          style={{ transform: "translateZ(80px)" }} 
          className="md:w-7/12 p-10 md:p-16 text-left"
        >
          {step === 'login' && (
            <>
              <div className="mb-10 text-left">
                <h2 className="text-4xl font-bold text-white mb-2">Welcome Back</h2>
                <div className="h-1.5 w-12 bg-amber-500 rounded-full mb-4" />
                <p className="text-slate-400 text-sm">Sign in to continue your journey</p>
              </div>

              {error && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-6 p-4 bg-red-500/10 border border-red-500/50 text-red-400 flex items-center gap-3 rounded-2xl"
                >
                  <AlertCircle size={20} />
                  <p className="text-sm font-semibold">{error}</p>
                </motion.div>
              )}

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2 text-left">
                  <label className="text-xs font-bold text-amber-500/80 uppercase block tracking-widest">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-amber-500 transition-all" size={20} />
                    <input 
                      type="email" 
                      required
                      value={formData.email}
                      className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:bg-white/10 transition-all text-left"
                      placeholder="name@company.com"
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2 text-left">
                  <label className="text-xs font-bold text-amber-500/80 uppercase block tracking-widest">
                    Password
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-amber-500 transition-all" size={20} />
                    <input 
                      type={showPassword ? "text" : "password"}
                      required
                      value={formData.password}
                      className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:bg-white/10 transition-all text-left"
                      placeholder="••••••••"
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-500 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-slate-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-white/5 text-amber-500 focus:ring-amber-500 focus:ring-offset-0"
                    />
                    <span>Remember me</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={handleForgotPassword}
                    className="text-amber-500 hover:text-amber-400 transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>

                <motion.button 
                  whileHover={{ scale: 1.02, translateY: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit" 
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl text-white font-black text-lg shadow-[0_20px_40px_-10px_rgba(245,158,11,0.5)] flex items-center justify-center gap-3 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="animate-spin" /> : <LogIn size={22} />}
                  <span>{loading ? 'VERIFYING...' : 'LOGIN'}</span>
                </motion.button>
              </form>
            </>
          )}

          {step === 'new-password' && (
            <>
              <div className="mb-10 text-left">
                <h2 className="text-4xl font-bold text-white mb-2">Set New Password</h2>
                <div className="h-1.5 w-12 bg-amber-500 rounded-full mb-4" />
                <p className="text-slate-400 text-sm">This is your first login. Please set a new password.</p>
              </div>

              {error && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="mb-6 p-4 bg-red-500/10 border border-red-500/50 text-red-400 flex items-center gap-3 rounded-2xl"
                >
                  <AlertCircle size={20} />
                  <p className="text-sm font-semibold">{error}</p>
                </motion.div>
              )}

              <form onSubmit={handleSetNewPassword} className="space-y-6">
                <div className="space-y-2 text-left">
                  <label className="text-xs font-bold text-amber-500/80 uppercase block tracking-widest">
                    New Password
                  </label>
                  <div className="relative group">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-amber-500 transition-all" size={20} />
                    <input 
                      type={showNewPassword ? "text" : "password"}
                      required
                      minLength={6}
                      className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:bg-white/10 transition-all text-left"
                      placeholder="Minimum 6 characters"
                      value={tempData.new_password}
                      onChange={(e) => setTempData({...tempData, new_password: e.target.value})}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-500 transition-colors"
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-left">
                  <label className="text-xs font-bold text-amber-500/80 uppercase block tracking-widest">
                    Confirm Password
                  </label>
                  <div className="relative group">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-amber-500 transition-all" size={20} />
                    <input 
                      type={showNewPassword ? "text" : "password"}
                      required
                      minLength={6}
                      className="w-full pl-12 pr-12 py-4 bg-white/5 border border-white/10 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:bg-white/10 transition-all text-left"
                      placeholder="Re-enter password"
                      value={tempData.confirm_password}
                      onChange={(e) => setTempData({...tempData, confirm_password: e.target.value})}
                    />
                  </div>
                </div>

                <p className="text-xs text-slate-500 mt-1">Password must be at least 6 characters long</p>

                <motion.button 
                  whileHover={{ scale: 1.02, translateY: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit" 
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl text-white font-black text-lg shadow-[0_20px_40px_-10px_rgba(245,158,11,0.5)] flex items-center justify-center gap-3 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <Loader2 className="animate-spin" /> : <Key size={22} />}
                  <span>{loading ? 'UPDATING...' : 'SET PASSWORD'}</span>
                </motion.button>

                <button
                  type="button"
                  onClick={() => setStep('login')}
                  className="w-full text-slate-400 hover:text-white text-sm transition-colors mt-4 text-center"
                >
                  Back to Login
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>

      {/* Decorative Glow Follower */}
      <motion.div 
        className="fixed top-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full blur-[100px] pointer-events-none z-0"
        style={{
          x: useTransform(mouseXSpring, [-0.5, 0.5], [-200, 200]),
          y: useTransform(mouseYSpring, [-0.5, 0.5], [-200, 200]),
        }}
      />
    </div>
  );
};

export default Login;